import React, { useState, useEffect, useRef, useMemo } from "react";
import { Dropdown, Menu } from "antd";
import { TextField } from "@material-ui/core";
import classNames from "clsx";
import { IconPlus, IconEllipsis } from "@/components/Icon";
import { useTranslation } from "@/langs/useTranslation";
import { isPromise } from "@/helpers/isPromise";
import { toast } from "@/helpers/toast"
import styles from "./CategoryNode.module.less";

const CategoryNode = React.memo(
  ({
    nodeInfo,
    onChangeRename,
    onInputRename,
    onAddNode,
    onChangeDelete,
    onMoveNode,
    onNodeAdd,
    onNodeImport,
    setDraggable,
    searchValue,
    readOnly: readOnlyFromProp,
    editable,
    sortable,
    importable
  }) => {
    const inputRef = useRef(null);
    const { t } = useTranslation();

    const menuList = useMemo(() => {
      return [
        editable && {
          action: "add",
          text: t({ defaultMessage: "添加子门店" })
        },
        importable && {
          action: "import",
          text: t({ defaultMessage: "汇入子门店" })
        },
        !nodeInfo.isRoot &&
          editable && {
            action: "rename",
            text: t({ defaultMessage: "重命名" })
          },
        !nodeInfo.isRoot &&
          sortable && {
            action: "moveUp",
            text: t({ defaultMessage: "上移" })
          },
        !nodeInfo.isRoot &&
          sortable && {
            action: "moveDown",
            text: t({ defaultMessage: "下移" })
          },
        !nodeInfo.isRoot &&
          editable && {
            action: "delete",
            text: t({ defaultMessage: "删除" })
          }
      ].filter(Boolean);
    }, [nodeInfo.isRoot, editable, importable, sortable]);

    const [rename, setRename] = useState(false);
    const [renameLoading, setRenameLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const readOnly = readOnlyFromProp || menuList.length === 0;

    const handleRename = () => {
      if (readOnly || !editable || nodeInfo.isRoot) return;
      setDraggable(false);
      setRename(true);
    };

    const handleDelete = () => {
      onChangeDelete(nodeInfo, true);
    };

    const onRename = e => {
      const { value } = e.target;

      if (renameLoading) return;

      if (nodeInfo.edit && !value) {
        setDraggable(true);
        onChangeDelete(nodeInfo);
        return;
      }
      if (!value) {
        toast.info(t({ defaultMessage: "分类名称不能为空" }));
        return;
      }
      if (value.length > 500) {
        toast.info(
          t({
            defaultMessage: "分类名称请控制在500字符内"
          })
        );
        return;
      }
      if (nodeInfo.title === value && !nodeInfo.edit) {
        setRename(false);
        setDraggable(true);
        return;
      }

      let guessPromise;
      if (nodeInfo.edit) {
        guessPromise = onAddNode(nodeInfo, value);
      } else {
        guessPromise = onChangeRename(nodeInfo, value);
      }
      if (isPromise(guessPromise)) {
        setRenameLoading(true);
        guessPromise
          .then(() => {
            setRename(false);
            setDraggable(true);
          })
          .finally(() => {
            setRenameLoading(false);
          });
      } else {
        setRename(false);
        setDraggable(true);
      }
    };

    const onChange = e => {
      const { value } = e.target;
      setInputValue(value);
      onInputRename?.(value);
    };

    const handleMenu = action => {
      switch (action) {
        case "add":
          onNodeAdd(nodeInfo);
          break;
        case "import":
          onNodeImport(nodeInfo);
          break;
        case "moveUp":
          onMoveNode(nodeInfo.key, false);
          break;
        case "moveDown":
          onMoveNode(nodeInfo.key, true);
          break;
        case "rename":
          handleRename();
          break;
        case "delete":
          handleDelete();
          break;
        default:
          break;
      }
    };

    const renderMenu = () => {
      return (
        <Menu
          onClick={({ domEvent }) => {
            domEvent.stopPropagation();
            domEvent.preventDefault();
          }}
        >
          {menuList.map(item => {
            return (
              <Menu.Item
                key={item.action}
                onClick={({ domEvent }) => {
                  domEvent.stopPropagation();
                  domEvent.preventDefault();
                  handleMenu(item.action);
                }}
                className={styles.menuItem}
              >
                {item.action === "delete" ? (
                  <span className={styles.deleteText}>{item.text}</span>
                ) : (
                  <span>{item.text}</span>
                )}
              </Menu.Item>
            );
          })}
        </Menu>
      );
    };

    useEffect(() => {
      if (nodeInfo.edit || rename) {
        setInputValue(nodeInfo.title);
      }
    }, [nodeInfo.edit, rename]);

    const renderTitle = () => {
      if (searchValue) {
        const index = nodeInfo.title.indexOf(searchValue);
        const beforeStr = nodeInfo.title.substr(0, index);
        const afterStr = nodeInfo.title.substr(index + searchValue.length);
        const title =
          index > -1 ? (
            <span className={styles.title}>
              {beforeStr}
              <span className={styles.searchKeyword}>{searchValue}</span>
              {afterStr}
            </span>
          ) : (
            <span className={styles.title}>{nodeInfo.title}</span>
          );
        return title;
      }
      return <span className={styles.title}>{nodeInfo.title}</span>;
    };

    useEffect(() => {
      setTimeout(() => {
        const input = inputRef.current;
        if (input && (nodeInfo.edit || rename)) {
          input.focus();
          input.select();
        }
      });
    }, [nodeInfo.edit, rename]);

    if (rename || nodeInfo.edit) {
      return (
        <TextField
          size="small"
          variant="outlined"
          className={styles.input}
          value={inputValue}
          disabled={renameLoading}
          onKeyDown={e => {
            if (e.keyCode === 13) {
              onRename?.(e);
            }
          }}
          onClick={e => e.stopPropagation()}
          onBlur={onRename}
          onChange={onChange}
          inputRef={inputRef}
        />
      );
    }

    return (
      <Dropdown
        disabled={readOnly || nodeInfo.showAction === false}
        // overlay={renderMenu}
        menu={renderMenu}
        placement="bottomLeft"
        trigger={["contextMenu"]}
      >
        <div className={styles.nodeTitle} onDoubleClick={handleRename}>
          <div className={styles.renderTitle}>
            {renderTitle()}
            <span className={styles.num}>
              ({nodeInfo.count}
              {nodeInfo.subCount !== undefined ? `/${nodeInfo.subCount}` : null}
              )
            </span>
          </div>

          {!readOnly && nodeInfo.showAction !== false && (
            <div
              onClick={e => e.stopPropagation()}
              className={classNames(styles.nodeIcon, "treeNode-nodeIcon")}
            >
              <Dropdown
                // overlay={renderMenu}
                menu={renderMenu}
                placement="bottomLeft"
                trigger={["click"]}
              >
                <div className={styles.nodeTitleMenu}>
                  <IconEllipsis />
                </div>
              </Dropdown>
              {editable && (
                <span
                  className={styles.titleAdd}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onNodeAdd(nodeInfo);
                  }}
                >
                  <IconPlus />
                </span>
              )}
            </div>
          )}
        </div>
      </Dropdown>
    );
  }
);

export default CategoryNode;
